/**
 * Restarting the hub from the console (ADR 0088): what the confirmation says, and the restart
 * itself. A restart is done when a hub with a later start time answers /api/health, never when
 * any hub answers: the old one still answers for a moment after the request.
 */
import type { HubLifecycleState, HubRestartResult } from '@orgus/protocol';
import { formatInt } from '@orgus/ui';
import { ApiError, api } from '../../lib/api.ts';

export const RESTART_WAIT_MS = 120_000;
const POLL_MS = 1_000;

const count = (n: number, one: string, many: string): string => `${formatInt(n)} ${n === 1 ? one : many}`;

/** What a restart ends and what it leaves alone, in one or two sentences. */
export function consequence(s: Pick<HubLifecycleState, 'endsOnRestart' | 'staysConnected'>): string {
  const ends = s.endsOnRestart === 0 ? 'No session started from Orgus is running on this computer.' : `${count(s.endsOnRestart, 'session', 'sessions')} started from Orgus on this computer ${s.endsOnRestart === 1 ? 'ends' : 'end'}.`;
  const one = s.staysConnected === 1;
  const stays = s.staysConnected === 0 ? '' : ` ${count(s.staysConnected, 'other machine keeps its', 'other machines keep their')} sessions and ${one ? 'reconnects on its' : 'reconnect on their'} own.`;
  return `${ends}${stays}`;
}

/** Who starts the hub again, as one line for the dialog. */
export function startedBy(s: Pick<HubLifecycleState, 'restartedBy'>): string {
  if (!s.restartedBy) return '';
  return s.restartedBy.kind === 'supervisor' ? `${s.restartedBy.name} starts it again. This page reconnects, usually within a minute.` : 'It starts again by itself. This page reconnects, usually within a minute.';
}

export interface RestartDeps {
  fetchHealth?: () => Promise<number | null>;
  post?: () => Promise<HubRestartResult>;
  now?: () => number;
  sleep?: (ms: number) => Promise<void>;
  waitMs?: number;
}

/** The start time /api/health reports, or null while the hub does not answer. */
export async function healthStartedAt(): Promise<number | null> {
  try {
    const r = await fetch('/api/health', { cache: 'no-store' });
    if (!r.ok) return null;
    const b = (await r.json()) as { build?: { startedAt?: unknown } };
    return typeof b.build?.startedAt === 'number' ? b.build.startedAt : null;
  } catch {
    return null;
  }
}

/** Ask for the restart and wait until the new hub answers. Rejects with an ApiError that says what to do. */
export async function restartAndWait(d: RestartDeps = {}): Promise<void> {
  const health = d.fetchHealth ?? healthStartedAt;
  const post = d.post ?? (() => api<HubRestartResult>('/api/hub/restart', { method: 'POST', body: {} }));
  const now = d.now ?? Date.now;
  const sleep = d.sleep ?? ((ms: number) => new Promise<void>((r) => setTimeout(r, ms)));
  const before = await health();
  await post();
  const t0 = now();
  let sawDown = false;
  for (;;) {
    await sleep(POLL_MS);
    const at = await health();
    if (at === null) sawDown = true;
    else if (before === null ? sawDown : at > before) return;
    if (now() - t0 > (d.waitMs ?? RESTART_WAIT_MS)) {
      throw new ApiError({
        what: 'The hub did not come back.',
        why: `It has not answered for ${Math.round((d.waitMs ?? RESTART_WAIT_MS) / 60_000)} minutes.`,
        next: 'Start it again where it runs (orgus up), then reload this page.',
      });
    }
  }
}
